import { Button } from "@/components/ui/button";
import { CaretDown, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import NetworkBadge from "./NetworkBadge";
import ProgramDetails from "./ProgramDetails";
import { formatSiteplugPayout } from "./payoutDisplay";

/**
 * Single brand row — compact header (clickable, always rendered) plus
 * an expanded panel (rendered only when this row is the open one,
 * keeping the DOM lean on 6k-row portfolios).
 *
 * Payout headline logic:
 *   • CPC offers (offer_type === "cpc" OR payout_mode === "cpc")
 *     → teal headline, prefer cpc_payout_display.
 *   • Skimlinks brands → use payout_display from the upstream merchant.
 *   • Flat CPA → emerald headline, "$X.XX".
 *   • Passthrough → display the headline_payout/rates_summary verbatim.
 *   • Direct % → blue headline, custom_commission_pct.
 *
 * Visual: passthrough/Skimlinks rows look IDENTICAL to direct-rate
 * brands so publishers don't see the internal pub-split arrangement —
 * the network split is intentionally hidden.
 */
function copyLink(link) {
  navigator.clipboard.writeText(link);
  toast.success("Tracking link copied");
}

function computePayoutDisplay(b) {
  const isCpc = (b.offer_type || "").toLowerCase() === "cpc" || b.payout_mode === "cpc";
  const payoutValue = isCpc
    ? (b.cpc_payout_display || b.headline_payout || b.payout_display || "CPC")
    : b.network === "siteplug"
    ? formatSiteplugPayout(b)
    : b.network === "skimlinks"
    ? (b.payout_display || "See details")
    : b.payout_mode === "flat_cpa"
    ? `$${Number(b.pub_flat_amount || 0).toFixed(2)}`
    : b.payout_mode === "passthrough"
    ? (b.headline_payout || b.payout_display || b.rates_summary || "See details")
    : `${b.custom_commission_pct}%`;
  const payoutColor = isCpc
    ? "text-teal-700"
    : b.payout_mode === "flat_cpa"
    ? "text-emerald-700"
    : "text-[#002FA7]";
  return { isCpc, payoutValue, payoutColor };
}

export default function BrandRow({ brand, isOpen, onToggleOpen, onTryTracker }) {
  const b = brand;
  const { isCpc, payoutValue, payoutColor } = computePayoutDisplay(b);

  return (
    <div data-testid={`pub-brand-${b.brand_id}`}>
      {/* Compact row — single line, click to expand */}
      <button
        type="button"
        onClick={onToggleOpen}
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors ${
          isOpen ? "bg-slate-50" : ""
        } ${b.status === "paused" ? "opacity-60" : ""}`}
        data-testid={`pub-brand-row-${b.brand_id}`}
      >
        <CaretDown
          size={12}
          weight="bold"
          className={`shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
        <div className="min-w-0 flex-1 flex items-center gap-4">
          <div className="min-w-0 flex-1 flex items-baseline gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate">{b.brand_name}</span>
            {/* Network badge — only renders when admin has enabled
                disclose_networks for this pub (b.network present). */}
            <NetworkBadge network={b.network} />
            {/* Show advertiser only when it actually differs from brand name */}
            {b.advertiser_name &&
              b.advertiser_name.trim().toLowerCase() !== (b.brand_name || "").trim().toLowerCase() && (
                <span className="text-xs text-slate-400 truncate">· {b.advertiser_name}</span>
              )}
            {b.status === "paused" && (
              <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700">
                paused
              </span>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {b.payout_mode === "flat_cpa" && (
              <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                Flat CPA
              </span>
            )}
            {isCpc && (
              <span
                className="text-[9px] font-bold uppercase tracking-[0.14em] text-teal-800 bg-teal-100 border border-teal-200 px-1.5 py-0.5 rounded"
                title="CPC offer — earn per click"
                data-testid={`pub-cpc-badge-${b.brand_id}`}
              >
                CPC
              </span>
            )}
            {b.deeplink ? (
              <span
                className="text-[9px] font-bold uppercase tracking-[0.14em] text-sky-800 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded"
                title={`Deeplink supported — replace {url} in: ${b.deeplink}`}
                data-testid={`pub-deeplink-badge-${b.brand_id}`}
              >
                Deeplink
              </span>
            ) : (
              <span
                className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded"
                title="Deeplink not supported for this brand"
                data-testid={`pub-no-deeplink-badge-${b.brand_id}`}
              >
                No deeplink
              </span>
            )}
            <span className={`text-sm font-bold font-mono tabular-nums ${payoutColor}`}>
              {payoutValue}
            </span>
          </div>
        </div>
      </button>

      {/* Expanded panel — tracking link, slot URLs, tracker macros, program details */}
      {isOpen && (
        <div className="px-4 pb-4 pt-2 bg-slate-50/60 border-t border-slate-100" data-testid={`pub-brand-expanded-${b.brand_id}`}>
          {/* Per-action payout breakdown (passthrough only — direct % brands have a single headline rate) */}
          {b.payout_mode === "passthrough" && Array.isArray(b.payout_actions) && b.payout_actions.length > 0 && (
            <div className="mb-3 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`payout-actions-${b.brand_id}`}>
              <div className="grid grid-cols-[1fr_180px] gap-4 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold tracking-[0.18em] uppercase text-slate-700">
                <span>Action</span>
                <span className="text-right">Payout</span>
              </div>
              {b.payout_actions.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_180px] gap-4 px-3 py-2 border-b border-slate-100 last:border-0 text-[13px]">
                  <div className="text-slate-800">
                    {row.action || "—"}
                    {row.note && <div className="text-[11px] text-slate-500 mt-0.5">{row.note}</div>}
                  </div>
                  <span className="text-right font-mono font-medium text-[#002FA7]">{row.value || "—"}</span>
                </div>
              ))}
            </div>
          )}

          {/* Primary tracking link — only when assignment is active and a link is on file */}
          {b.status === "active" && b.tracking_link && (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white border border-slate-200 rounded px-3 py-2 font-mono text-xs text-slate-600 truncate">
                {b.tracking_link}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); copyLink(b.tracking_link); }}
                className="shrink-0 h-9"
                data-testid={`copy-link-${b.brand_id}`}
              >
                <Copy size={14} className="mr-1" /> Copy Link
              </Button>
            </div>
          )}

          {/* Deeplink template — null/empty means not supported */}
          {b.status === "active" && b.deeplink && (
            <div className="mt-2" data-testid={`pub-deeplink-url-${b.brand_id}`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                Deeplink template — replace {"{url}"} with the destination page
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-white border border-sky-200 rounded px-3 py-2 font-mono text-xs text-slate-600 truncate" title={b.deeplink}>
                  {b.deeplink}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); copyLink(b.deeplink); }}
                  className="shrink-0 h-9"
                  data-testid={`copy-deeplink-${b.brand_id}`}
                >
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
            </div>
          )}

          {/* Pub-controlled tracker macros (e.g. AdMedia q/clickid/ref) */}
          {b.status === "active" && b.tracker_macros && Object.keys(b.tracker_macros).length > 0 && (
            <div className="border border-violet-200 bg-violet-50/40 rounded px-3 py-2.5 text-[11px]" data-testid={`tracker-macros-${b.brand_id}`}>
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="font-bold uppercase tracking-[0.2em] text-violet-700 text-[10px]">
                  Optional pub-controlled params
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onTryTracker(b); }}
                  className="text-[10px] font-bold uppercase tracking-[0.15em] text-violet-700 hover:text-violet-900 underline decoration-violet-300 hover:decoration-violet-700 underline-offset-2"
                  data-testid={`try-tracker-${b.brand_id}`}
                >
                  Try this URL →
                </button>
              </div>
              <ul className="space-y-0.5">
                {Object.entries(b.tracker_macros).map(([k, desc]) => (
                  <li key={k} className="font-mono text-slate-700">
                    <span className="text-violet-700 font-semibold">&amp;{k}=</span>
                    <span className="text-slate-500 font-sans"> — {desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional tracker slot URLs — one per active slot the pub has opened */}
          {b.status === "active" && Array.isArray(b.slot_urls) && b.slot_urls.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-slate-100" data-testid={`slot-urls-${b.brand_id}`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                Additional tracker slots — distinct sub_id per URL
              </div>
              {b.slot_urls.map((s) => (
                <div key={s.sub_id} className="flex items-center gap-2">
                  <span className="inline-flex w-7 h-7 shrink-0 items-center justify-center bg-slate-900 text-white rounded text-xs font-bold">{s.label || "·"}</span>
                  <div className="flex-1 bg-white border border-slate-200 rounded px-3 py-2 font-mono text-xs text-slate-600 truncate">
                    {s.tracker_url || s.direct_url}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); copyLink(s.tracker_url || s.direct_url); }}
                    className="shrink-0 h-9"
                    data-testid={`copy-slot-link-${b.brand_id}-${s.sub_id}`}
                  >
                    <Copy size={14} className="mr-1" /> Copy
                  </Button>
                </div>
              ))}
            </div>
          )}

          <ProgramDetails brand={b} />
        </div>
      )}
    </div>
  );
}
